"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { SupportTicketThread } from "./SupportTicketThread";
import type { SupportTicketSummary } from "./types";

interface Props {
  ticket: SupportTicketSummary;
  /** Where the "← All tickets" link goes. Defaults to "/dashboard/support/tickets". */
  ticketsPath?: string;
  /** Endpoint the reply form posts to. Defaults to `/api/support/ticket/{id}/message`. */
  endpoint?: string;
  /** Override viewer name for the reply byline. */
  viewerName?: string;
  /** Endpoint the reopen button posts to. Defaults to `/api/support/ticket/{id}/reopen`. */
  reopenEndpoint?: string;
  /** Endpoint the "This solved my issue" button posts to. Defaults to
   *  `/api/support/ticket/{id}/mark-resolved`. */
  resolveEndpoint?: string;
}

const STATUS_META: Record<
  string,
  {
    label: string;
    tint: string;
    bar: string;
    hint: string;
  }
> = {
  OPEN: {
    label: "Open",
    tint: "bg-blue-100 text-blue-800 border-blue-200",
    bar: "bg-blue-500",
    hint: "We've got it and we're getting back to you.",
  },
  IN_PROGRESS: {
    label: "In progress",
    tint: "bg-amber-100 text-amber-800 border-amber-200",
    bar: "bg-amber-500",
    hint: "Someone's actively looking into this.",
  },
  WAITING_ON_USER: {
    label: "Waiting on you",
    tint: "bg-purple-100 text-purple-800 border-purple-200",
    bar: "bg-purple-500",
    hint: "We need a bit more from you to keep going. Reply below.",
  },
  RESOLVED: {
    label: "Resolved",
    tint: "bg-emerald-100 text-emerald-800 border-emerald-200",
    bar: "bg-emerald-500",
    hint: "This one's wrapped — reply if it's not.",
  },
  CLOSED: {
    label: "Closed",
    tint: "bg-gray-200 text-gray-700 border-gray-300",
    bar: "bg-gray-400",
    hint: "This ticket is closed.",
  },
};

const PRIORITY_DOT: Record<string, string> = {
  LOW: "bg-emerald-400",
  MEDIUM: "bg-amber-400",
  HIGH: "bg-orange-500",
  URGENT: "bg-red-500",
};

const CATEGORY_LABEL: Record<string, string> = {
  ACCOUNT: "Account",
  BILLING: "Billing",
  BOOKING: "Booking",
  TECHNICAL: "Technical",
  QUESTION: "Question",
  FEEDBACK: "Feedback",
  OTHER: "Other",
};

function asDate(iso: string): Date {
  const hasOffset = /Z$|[+-]\d{2}:?\d{2}$/.test(iso);
  return new Date(hasOffset ? iso : `${iso}Z`);
}

function formatLocal(iso: string): string {
  try {
    return new Intl.DateTimeFormat("en", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(asDate(iso));
  } catch {
    return iso;
  }
}

function formatLocalDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("en", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(asDate(iso));
  } catch {
    return iso;
  }
}

export function SubmitterTicketDetail({
  ticket,
  ticketsPath = "/dashboard/support/tickets",
  endpoint,
  viewerName,
  reopenEndpoint,
  resolveEndpoint,
}: Props) {
  const router = useRouter();
  const status = STATUS_META[ticket.status] ?? STATUS_META.OPEN!;
  const priority = (ticket.priority || "MEDIUM").toUpperCase();
  const category = CATEGORY_LABEL[ticket.category] ?? ticket.category ?? "General";

  const canReopen = ticket.status === "RESOLVED" || ticket.status === "CLOSED";
  const canMarkResolved =
    ticket.status === "OPEN" ||
    ticket.status === "IN_PROGRESS" ||
    ticket.status === "WAITING_ON_USER";
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [reopening, setReopening] = useState(false);
  const [reopenError, setReopenError] = useState<string | null>(null);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveReason, setResolveReason] = useState("");
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  const searchParams = useSearchParams();
  // Auto-open the resolve panel when the URL carries `?resolve=1`
  // (the shortcut baked into the admin-reply email). Also strips the
  // param so a page refresh doesn't keep re-opening it. Query params
  // beat a URL fragment for this — they survive across all URL shapes
  // and are visible to the server too (useful for future refactors).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!canMarkResolved) return;
    if (searchParams?.get("resolve") !== "1") return;
    setResolveOpen(true);
    const url = new URL(window.location.href);
    url.searchParams.delete("resolve");
    const cleaned =
      url.pathname +
      (url.searchParams.toString() ? `?${url.searchParams.toString()}` : "") +
      url.hash;
    window.history.replaceState({}, "", cleaned);
  }, [canMarkResolved, searchParams]);

  async function handleReopen() {
    setReopenError(null);
    setReopening(true);
    try {
      const url = reopenEndpoint ?? `/api/support/ticket/${ticket.id}/reopen`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: reopenReason.trim() || null }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error ?? "Failed to reopen");
      }
      // Server appended a SYSTEM message and flipped status — refresh
      // to pick up both. The thread renders from server-fetched data.
      router.refresh();
      setReopenOpen(false);
      setReopenReason("");
    } catch (err) {
      setReopenError(err instanceof Error ? err.message : "Failed to reopen");
    } finally {
      setReopening(false);
    }
  }

  async function handleMarkResolved() {
    setResolveError(null);
    setResolving(true);
    try {
      const url =
        resolveEndpoint ?? `/api/support/ticket/${ticket.id}/mark-resolved`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: resolveReason.trim() || null }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error ?? "Failed to mark resolved");
      }
      router.refresh();
      setResolveOpen(false);
      setResolveReason("");
    } catch (err) {
      setResolveError(
        err instanceof Error ? err.message : "Failed to mark resolved",
      );
    } finally {
      setResolving(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <Link
          href={ticketsPath}
          className="inline-flex items-center gap-1 rounded-full bg-white/60 px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm ring-1 ring-gray-200 backdrop-blur hover:bg-white"
        >
          ← All tickets
        </Link>
      </div>

      {/* Hero */}
      <header className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
        <div className={`h-1.5 w-full ${status.bar}`} aria-hidden />
        <div className="p-6 sm:p-8">
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-medium text-gray-500">
            <span className="font-mono">Ticket #{ticket.ticketNumber}</span>
            <span aria-hidden>·</span>
            <span>Submitted {formatLocal(ticket.createdAt)}</span>
            {ticket.updatedAt && ticket.updatedAt !== ticket.createdAt && (
              <>
                <span aria-hidden>·</span>
                <span>Last activity {formatLocal(ticket.updatedAt)}</span>
              </>
            )}
          </div>

          <h1 className="mt-2 text-2xl font-semibold tracking-tight text-gray-900 sm:text-3xl">
            {ticket.subject}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${status.tint}`}
            >
              {status.label}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
              <span
                className={`h-1.5 w-1.5 rounded-full ${PRIORITY_DOT[priority] ?? PRIORITY_DOT.MEDIUM}`}
              />
              {priority.toLowerCase()}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200">
              {category}
            </span>
            {ticket.dueAt && (() => {
              const isOverdue =
                ticket.status !== "RESOLVED" &&
                ticket.status !== "CLOSED" &&
                asDate(ticket.dueAt).getTime() < Date.now();
              return (
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${
                    isOverdue
                      ? "border-red-200 bg-red-50 text-red-800"
                      : "border-indigo-200 bg-indigo-50 text-indigo-800"
                  }`}
                  title={isOverdue ? "Past the resolve-by date" : "Resolve by"}
                >
                  <span aria-hidden>📅</span>
                  {isOverdue ? "Overdue · " : "Resolve by "}
                  {formatLocalDate(ticket.dueAt)}
                </span>
              );
            })()}
          </div>

          <p className="mt-4 text-sm leading-relaxed text-gray-600">{status.hint}</p>
        </div>
      </header>

      {/* Mark-resolved panel — only visible when the ticket is still
          active (OPEN / IN_PROGRESS / WAITING_ON_USER). Lets the
          submitter close their own loop without waiting on ops to
          flip the status manually. Ops used to have to mark every
          ticket resolved themselves, which meant ~24 tickets rotting
          in WAITING_ON_USER at any given time (audit 2026-09-06). */}
      {canMarkResolved && (
        <div className="rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-white p-4 shadow-sm">
          {!resolveOpen ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-gray-900">
                  Everything sorted?
                </div>
                <div className="text-xs text-gray-600">
                  Mark this ticket as resolved so we know it's done. You can
                  always reopen it later.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setResolveOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700"
              >
                This solved it
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  Anything you want to add?
                </div>
                <div className="text-xs text-gray-600">
                  Optional — a quick note about what fixed it helps the next
                  person who hits the same issue.
                </div>
              </div>
              <textarea
                value={resolveReason}
                onChange={(e) => setResolveReason(e.target.value)}
                rows={3}
                placeholder="e.g. Reset the password and it worked. Thanks!"
                disabled={resolving}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
              />
              {resolveError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                  {resolveError}
                </div>
              )}
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setResolveOpen(false);
                    setResolveReason("");
                    setResolveError(null);
                  }}
                  disabled={resolving}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleMarkResolved}
                  disabled={resolving}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-60"
                >
                  {resolving ? "Marking resolved…" : "Mark resolved"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reopen panel — only visible on RESOLVED/CLOSED. Lets the
          submitter flip the ticket back to OPEN if the issue isn't
          actually fixed, with an optional reason that gets posted to
          the thread for context. */}
      {canReopen && (
        <div className="rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-white to-white p-4 shadow-sm">
          {!reopenOpen ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-gray-900">
                  Not actually resolved?
                </div>
                <div className="text-xs text-gray-600">
                  Reopen this ticket and our team will pick it back up.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setReopenOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full bg-amber-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-amber-700"
              >
                Reopen ticket
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  Tell us what's still wrong
                </div>
                <div className="text-xs text-gray-600">
                  Optional, but it helps us pick up where we left off.
                </div>
              </div>
              <textarea
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                rows={3}
                placeholder="e.g. The fix didn't work for me — still seeing the same error."
                disabled={reopening}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100"
              />
              {reopenError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                  {reopenError}
                </div>
              )}
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setReopenOpen(false);
                    setReopenReason("");
                    setReopenError(null);
                  }}
                  disabled={reopening}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleReopen}
                  disabled={reopening}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-amber-700 disabled:opacity-60"
                >
                  {reopening ? "Reopening…" : "Reopen ticket"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Thread (original report + replies + reply form) */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
        <SupportTicketThread
          ticket={ticket}
          isAdmin={false}
          endpoint={endpoint}
          viewerName={viewerName}
          hideHeader
          onReply={
            // Server auto-reopens RESOLVED tickets on a submitter reply.
            // Refresh so the status pill + reopen panel reflect OPEN
            // instead of the stale RESOLVED state.
            canReopen ? () => router.refresh() : undefined
          }
        />
      </div>
    </div>
  );
}
